import { CLINIC_API_SCOPES, WEBHOOK_EVENT_LABELS_FA, type ClinicApiScope } from '@bookora/shared';
import type { StatusTone } from '@bookora/ui';

import type {
  ApiKey,
  DeliveryStatus,
  KeyStatus,
  WebhookDelivery,
  WebhookDisabledReason,
} from './api';

/** The tab's label, also the hub section's destination. */
export const INTEGRATIONS_TAB_LABEL = 'اتصال‌ها (API)';
export const HUB_SECTION_TITLE = 'اتصال به سایت و سامانه‌های دیگه';
/** Where the hub sends the person: the integrations tab of settings. */
export const INTEGRATIONS_TAB_PATH = '/settings?tab=integrations';

/** Scope labels come from the shared table, so the panel, the API and the docs agree. */
export function scopeLabel(scope: ClinicApiScope): string {
  return CLINIC_API_SCOPES.find((s) => s.key === scope)?.labelFa ?? scope;
}

/** A public event type (or `ping`) in Persian; an unknown string is shown as-is. */
export function eventLabel(event: string): string {
  if (event === 'ping') return 'ارسال آزمایشی';
  return (WEBHOOK_EVENT_LABELS_FA as Record<string, string>)[event] ?? event;
}

export const KEY_STATUS: Readonly<Record<KeyStatus, { label: string; tone: StatusTone }>> = {
  ACTIVE: { label: 'فعال', tone: 'success' },
  EXPIRED: { label: 'منقضی', tone: 'warning' },
  REVOKED: { label: 'باطل‌شده', tone: 'inert' },
};

export const DELIVERY_STATUS: Readonly<
  Record<DeliveryStatus, { label: string; tone: StatusTone }>
> = {
  PENDING: { label: 'در صف', tone: 'progress' },
  // «تحویل شد», not «رسید» — which also reads as «رسید» (a receipt).
  SUCCEEDED: { label: 'تحویل شد', tone: 'success' },
  FAILED: { label: 'ناموفق', tone: 'danger' },
  SKIPPED: { label: 'فرستاده نشد', tone: 'inert' },
};

export const WEBHOOK_DISABLED_COPY: Readonly<Record<WebhookDisabledReason, string>> = {
  MANUAL: 'غیرفعال‌شده از سوی کلینیک',
  AUTO_FAILURES: 'به‌خاطر خطاهای پشت‌سرهم خودکار متوقف شد',
  PLATFORM: 'متوقف‌شده از سوی پشتیبانی بوکورا',
};

/** What the activity drawer calls the audit actions a key can leave behind. */
const ACTIVITY_LABELS: Readonly<Record<string, string>> = {
  'appointment.created': 'ثبت نوبت',
  'appointment.updated': 'تغییر یا جابه‌جایی نوبت',
  'appointment.cancelled': 'لغو نوبت',
  'patient.created': 'ثبت بیمار جدید',
  'patient.viewed': 'دیدن مشخصات بیمار',
  'clinic_api.patient_lookup': 'جست‌وجوی بیمار',
};

export function activityLabel(action: string): string | null {
  return ACTIVITY_LABELS[action] ?? null;
}

/** Expiry choices of the create drawer. «یک سال» is the default. */
export const EXPIRY_OPTIONS: readonly { value: 'd90' | 'd365' | 'never'; label: string }[] = [
  { value: 'd90', label: '۹۰ روز' },
  { value: 'd365', label: 'یک سال' },
  { value: 'never', label: 'بدون انقضا' },
];

/** Grace choices of the rotate dialogs. */
export const GRACE_OPTIONS: readonly { value: 0 | 24 | 168; label: string }[] = [
  { value: 0, label: 'بدون مهلت' },
  { value: 24, label: '۲۴ ساعت' },
  { value: 168, label: '۷ روز' },
];

export const DATA_ACCESS_ACK =
  'می‌دونم هر سامانه‌ای که این کلید رو داشته باشه، به همین اطلاعات بیماران و نوبت‌ها دسترسی داره و مسئولیت نگهداری امن اون با کلینیک است.';

export const SECRET_ONCE_WARNING =
  'این کلید فقط همین یک بار نمایش داده می‌شه. اون رو در جای امنی ذخیره کنید؛ در صورت گم شدن، کلید جدید بسازید و جایگزین کنید.';

export const SIGNING_SECRET_ONCE_WARNING =
  'این رمز امضا فقط همین یک بار نمایش داده می‌شه. اون رو در سامانهٔ گیرنده ذخیره کنید؛ در صورت گم شدن، رمز جدید بسازید و جایگزین کنید.';

export const CREATOR_LEFT = 'سازندهٔ این کلید دیگه عضو کلینیک نیست.';

/**
 * Why a key is «باطل‌شده», for the status chip's hint: who revoked it (the
 * clinic, or Bookora support from the console — which also alerted the
 * clinic) and the reason they wrote, if any. null for a key not revoked.
 */
export function revokedHint(
  key: Pick<ApiKey, 'effectiveStatus' | 'revokedBy' | 'revokeReason'>,
): string | null {
  if (key.effectiveStatus !== 'REVOKED') return null;
  const who =
    key.revokedBy === 'platform'
      ? 'پشتیبانی بوکورا این کلید رو باطل کرد'
      : 'این کلید از سوی کلینیک باطل شد';
  const reason = key.revokeReason?.trim();
  return reason ? `${who}. دلیل: «${reason}»` : `${who}.`;
}

/** The delivery-log filter: all, or one status. */
export const DELIVERY_FILTERS: readonly { value: DeliveryStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'همه' },
  { value: 'FAILED', label: 'ناموفق' },
  { value: 'PENDING', label: 'در صف' },
  { value: 'SUCCEEDED', label: 'تحویل شد' },
  { value: 'SKIPPED', label: 'فرستاده نشد' },
];

/**
 * The «پاسخ گیرنده» cell of the delivery log, in Persian. The server's
 * `lastError` is a technical line for support (`timeout after 10000 ms`,
 * `refused: PRIVATE_ADDRESS`, `ECONNREFUSED: …`, `HTTP 302 — redirects are not
 * followed`); the clinic owner reading the log needs to know what to fix. The
 * HTTP code stays alongside as an LTR code (a developer asks for «۵۰۰» by that
 * number), and an error this table does not know is shown as-is.
 */
export interface DeliveryOutcome {
  /** Persian sentence, or null when the code alone says it (a 2xx). */
  text: string | null;
  /** The HTTP status, when the receiver answered. */
  statusCode: number | null;
  /** The raw line, when nothing above could translate it. */
  raw: string | null;
}

export function deliveryOutcome(
  row: Pick<WebhookDelivery, 'status' | 'lastStatusCode' | 'lastError'>,
): DeliveryOutcome {
  const code = row.lastStatusCode;
  const error = row.lastError?.trim() ?? '';
  if (code !== null && code >= 200 && code < 300)
    return { text: null, statusCode: code, raw: null };
  if (code !== null && code >= 300 && code < 400) {
    return {
      text: 'گیرنده درخواست رو به آدرس دیگری هدایت کرده. بوکورا این تغییر آدرس رو دنبال نمی‌کنه؛ آدرس نهایی رو ثبت کنید.',
      statusCode: code,
      raw: null,
    };
  }
  if (code !== null && code >= 400) {
    return {
      text:
        code >= 500
          ? 'سرور گیرنده خطا داد. گزارش خطای سامانهٔ گیرنده رو بررسی کنید.'
          : 'گیرنده پیام رو نپذیرفت. آدرس و تنظیمات بررسی امضا رو در سیستم گیرنده بررسی کنید.',
      statusCode: code,
      raw: null,
    };
  }
  if (error === '') return { text: null, statusCode: null, raw: null };
  if (/^timeout/i.test(error)) {
    return { text: 'گیرنده در ۱۰ ثانیه پاسخ نداد.', statusCode: null, raw: null };
  }
  if (/^refused:/i.test(error)) {
    return {
      text: 'آدرس گیرنده داخلی، خصوصی یا غیرقابل دسترسی بود. ارسال به این آدرس مجاز نیست و پیام فرستاده نشد.',
      statusCode: null,
      raw: null,
    };
  }
  if (/^endpoint disabled/i.test(error)) {
    return { text: 'نشانی هنگام ارسال غیرفعال بود.', statusCode: null, raw: null };
  }
  if (/^plan lacks/i.test(error)) {
    return { text: 'پلن کلینیک در اون زمان وب‌هوک نداشت.', statusCode: null, raw: null };
  }
  if (/^(ENOTFOUND|EAI_AGAIN)\b/.test(error)) {
    return { text: 'دامنهٔ گیرنده پیدا نشد.', statusCode: null, raw: null };
  }
  if (/^(ECONNREFUSED|ECONNRESET|EHOSTUNREACH|ENETUNREACH)\b/.test(error)) {
    return { text: 'به سرور گیرنده وصل نشد.', statusCode: null, raw: null };
  }
  if (/^(CERT_|DEPTH_ZERO|SELF_SIGNED|UNABLE_TO_VERIFY|ERR_TLS)/.test(error)) {
    return { text: 'گواهی امنیتی سرور گیرنده معتبر نیست.', statusCode: null, raw: null };
  }
  return { text: null, statusCode: null, raw: error };
}
